import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { api_url } from '../conf/conf';
import './LocalAdvanceReport.css';

const MANUAL_FIELDS = [
  ['organizationName', 'Организация'],
  ['ceoPosition', 'Должность руководителя'],
  ['ceoName', 'ФИО руководителя'],
  ['accountablePerson', 'Подотчётное лицо'],
  ['accountablePosition', 'Должность сотрудника'],
  ['chiefAccountant', 'Главный бухгалтер'],
  ['accountant', 'Бухгалтер'],
  ['cashierAccountant', 'Бухгалтер-кассир'],
];

const initialManualData = Object.fromEntries(MANUAL_FIELDS.map(([key]) => [key, '']));

function money(value, currency = 'RUB') {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: currency || 'RUB',
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function LocalAdvanceReport() {
  const [health, setHealth] = useState({ loading: true, ok: false, message: '' });
  const [files, setFiles] = useState([]);
  const [instruction, setInstruction] = useState('');
  const [manualData, setManualData] = useState(initialManualData);
  const [loading, setLoading] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const report = result?.assistantResponse?.advanceReport?.reportData;
  const editedFile = result?.assistantResponse?.editedFile;
  const downloadUrl = editedFile
    ? `${api_url}/local-advance-report/file?url=${encodeURIComponent(editedFile)}`
    : '';
  const selectedSize = useMemo(
    () => files.reduce((total, file) => total + file.size, 0),
    [files]
  );

  async function checkHealth() {
    setHealth((current) => ({ ...current, loading: true, message: '' }));
    try {
      const response = await axios.get(`${api_url}/local-advance-report/health`, {
        timeout: 20000,
      });
      setHealth({
        loading: false,
        ok: Boolean(response.data?.ok),
        message: response.data?.ok
          ? `${response.data.model} · ${response.data.latencyMs} мс`
          : response.data?.message || 'Модель не готова',
      });
    } catch (requestError) {
      setHealth({
        loading: false,
        ok: false,
        message: requestError.response?.data?.message || requestError.message,
      });
    }
  }

  useEffect(() => {
    checkHealth();
  }, []);

  async function waitForJob(jobId, pollAfterMs = 3000) {
    const deadline = Date.now() + 15 * 60 * 1000;

    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, pollAfterMs));
      const response = await axios.get(
        `${api_url}/local-advance-report/jobs/${encodeURIComponent(jobId)}`,
        { timeout: 30000 }
      );
      const job = response.data;

      if (job.status === 'completed') {
        return job.result;
      }
      if (job.status === 'failed') {
        throw new Error(job.error?.message || 'Локальная модель не смогла обработать документы.');
      }

      setProcessingMessage(
        job.status === 'processing'
          ? 'Qwen извлекает расходы. На CPU это может занять несколько минут…'
          : 'Задание ожидает запуска…'
      );
    }

    throw new Error('Обработка занимает больше 15 минут. Проверьте состояние Qwen на AI-сервере.');
  }

  async function submit(event) {
    event.preventDefault();
    if (!files.length) {
      setError('Добавьте хотя бы один чек, документ или файл с расходами.');
      return;
    }

    setLoading(true);
    setProcessingMessage('Загружаем документы…');
    setError('');
    setResult(null);

    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('text', instruction);
    formData.append('manualData', JSON.stringify(manualData));

    try {
      const response = await axios.post(
        `${api_url}/local-advance-report/test`,
        formData,
        { timeout: 120000 }
      );
      if (response.data?.jobId) {
        setProcessingMessage('Задание поставлено в очередь…');
        const completedResult = await waitForJob(
          response.data.jobId,
          Number(response.data.pollAfterMs || 3000)
        );
        setResult(completedResult);
      } else {
        setResult(response.data);
      }
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
        requestError.message ||
        'Не удалось сформировать авансовый отчёт.'
      );
    } finally {
      setLoading(false);
      setProcessingMessage('');
    }
  }

  return (
    <main className="lar-page">
      <header className="lar-header">
        <div>
          <p className="lar-kicker">Admin POC</p>
          <h1>Локальный ИИ · авансовый отчёт</h1>
          <p>Извлечение расходов выполняет Qwen на отдельном сервере. Пользовательские роуты не используются.</p>
        </div>
        <button className="lar-health" type="button" onClick={checkHealth}>
          <span className={`lar-dot ${health.loading ? 'loading' : health.ok ? 'ok' : 'error'}`} />
          {health.loading ? 'Проверка…' : health.ok ? `Готов: ${health.message}` : `Недоступен: ${health.message}`}
        </button>
      </header>

      <form className="lar-layout" onSubmit={submit}>
        <section className="lar-card">
          <h2>1. Документы и инструкция</h2>
          <label className="lar-drop">
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.webp,.pdf,.docx,.xlsx,.txt,.csv,.json,.xml"
              onChange={(event) => setFiles(Array.from(event.target.files || []))}
            />
            <strong>Выберите чеки и документы</strong>
            <span>Фото, PDF, DOCX, XLSX и текстовые файлы · до 20 файлов</span>
          </label>

          {files.length > 0 && (
            <div className="lar-files">
              <span>{files.length} файл(ов), {(selectedSize / 1024 / 1024).toFixed(1)} МБ</span>
              <ul>{files.map((file) => <li key={`${file.name}-${file.size}`}>{file.name}</li>)}</ul>
            </div>
          )}

          <label className="lar-label">
            Дополнительная инструкция
            <textarea
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              placeholder="Например: это командировка с 20 по 23 июня, аванс выдан 30 000 рублей."
              rows={4}
            />
          </label>
        </section>

        <section className="lar-card">
          <h2>2. Реквизиты</h2>
          <p className="lar-muted">Можно оставить пустыми — ИИ попробует найти их в документах.</p>
          <div className="lar-fields">
            {MANUAL_FIELDS.map(([key, label]) => (
              <label className="lar-label" key={key}>
                {label}
                <input
                  value={manualData[key]}
                  onChange={(event) => setManualData({
                    ...manualData,
                    [key]: event.target.value,
                  })}
                />
              </label>
            ))}
          </div>
        </section>

        <div className="lar-actions">
          <button className="lar-submit" type="submit" disabled={loading || !health.ok}>
            {loading ? processingMessage || 'Qwen обрабатывает документы…' : 'Сформировать отчёт'}
          </button>
          {!health.ok && !health.loading && <span>Сначала восстановите соединение с локальной моделью.</span>}
        </div>
      </form>

      {error && <div className="lar-error">{error}</div>}

      {report && (
        <section className="lar-result">
          <div className="lar-result-heading">
            <div>
              <p className="lar-kicker">Результат</p>
              <h2>Отчёт сформирован</h2>
              <p>
                {result.model} · {(Number(result.processingTimeMs || 0) / 1000).toFixed(1)} сек ·{' '}
                {result.assistantResponse?.tokenUsage?.total || 0} токенов
              </p>
            </div>
            {downloadUrl && <a className="lar-download" href={downloadUrl}>Скачать XLSX</a>}
          </div>

          <div className="lar-totals">
            <div><span>Расходы</span><strong>{money(report.totals?.totalExpenses, report.reportMeta?.currency)}</strong></div>
            <div><span>Выданный аванс</span><strong>{money(report.totals?.advanceIssued, report.reportMeta?.currency)}</strong></div>
            <div><span>Остаток</span><strong>{money(report.totals?.balance, report.reportMeta?.currency)}</strong></div>
          </div>

          <div className="lar-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Документ</th>
                  <th>Поставщик</th>
                  <th>Назначение</th>
                  <th>Сумма</th>
                </tr>
              </thead>
              <tbody>
                {(report.expenses || []).map((expense, index) => (
                  <tr key={`${expense.documentNumber}-${index}`}>
                    <td>{expense.date || '—'}</td>
                    <td>{[expense.documentType, expense.documentNumber].filter(Boolean).join(' № ') || '—'}</td>
                    <td>{expense.supplier || '—'}</td>
                    <td>{expense.purpose || '—'}</td>
                    <td>{money(expense.amount, report.reportMeta?.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </main>
  );
}

export default LocalAdvanceReport;
